import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from '../lib/router';
import { getDashboardDef, verifyDashboardAccess } from '../lib/dashboardAuth';

interface DashboardGuardProps {
  path: string;
  children: React.ReactNode;
}

/**
 * Gate that prevents rendering a dashboard until the visitor's authentication
 * and role have been verified. While checking it shows a loading state; on
 * failure it redirects to the matching login page instead of exposing the
 * dashboard. This closes the "type a dashboard URL and you're in" hole.
 */
export const DashboardGuard: React.FC<DashboardGuardProps> = ({ path, children }) => {
  const { push } = useRouter();
  const [status, setStatus] = useState<'checking' | 'authorized' | 'denied'>('checking');
  const [loginPath, setLoginPath] = useState<string>('/login');
  const [denyReason, setDenyReason] = useState<string>('Zugriff konnte nicht geprüft werden.');
  const lastPath = useRef<string>('');

  useEffect(() => {
    const def = getDashboardDef(path);
    // Not a guarded dashboard route → render as-is.
    if (!def) { setStatus('authorized'); return; }

    let cancelled = false;
    lastPath.current = path;
    setStatus('checking');
    setLoginPath(def.loginPath);

    verifyDashboardAccess(def).then((result) => {
      if (cancelled || lastPath.current !== path) return;
      if (result === 'authorized') {
        setStatus('authorized');
      } else {
        setDenyReason('Deine Session ist nicht aktiv oder passt nicht zu diesem Dashboard. Bitte melde dich erneut an.');
        setStatus('denied');
      }
    });

    return () => { cancelled = true; };
  }, [path]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center px-6">
          <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          <div className="text-sm text-gray-400">Zugriff wird geprüft…</div>
        </div>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="max-w-lg mx-auto px-6 text-center space-y-5">
          <div className="mx-auto h-14 w-14 rounded-full border border-red-500/30 bg-red-500/10 flex items-center justify-center">
            <div className="h-5 w-5 rounded-full bg-red-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Dashboard nicht verfügbar</h2>
            <p className="mt-3 text-sm text-gray-400">{denyReason}</p>
          </div>
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              onClick={() => push(loginPath)}
              className="px-5 py-3 rounded-full bg-white text-black font-semibold hover:bg-gray-200 transition-colors"
            >
              Zum Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default DashboardGuard;
